import { logger } from '../utils/logger';

// Sentry est optionnel - installer avec: npm install @sentry/react @sentry/tracing
interface SentryModule {
  init: (config: Record<string, unknown>) => void;
  captureException: (error: Error, context?: { extra?: Record<string, unknown> }) => void;
  captureMessage: (message: string, level: string) => void;
}

let Sentry: SentryModule | null = null;

try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  Sentry = require('@sentry/react') as SentryModule;
} catch {
  // Sentry non installé - monitoring désactivé en développement
  // Pour installer: npm install @sentry/react @sentry/tracing
}

export const initSentry = () => {
  if (import.meta.env.MODE === 'production' && Sentry) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN, // À configurer dans .env
      integrations: [
        // new BrowserTracing(), // Commenté car @sentry/tracing non installé
        // new Sentry.Replay({
        //   maskAllText: true,
        //   blockAllMedia: true,
        // }),
      ],
      
      // Performance Monitoring
      tracesSampleRate: 0.1, // 10% des transactions
      
      // Session Replay
      replaysSessionSampleRate: 0.01, // 1% des sessions
      replaysOnErrorSampleRate: 1.0, // 100% des sessions avec erreurs
      
      // Environment
      environment: import.meta.env.MODE,
      
      // Release tracking
      release: import.meta.env.VITE_APP_VERSION || '1.0.0',
      
      // Filtrage des erreurs
      beforeSend(event: Record<string, unknown>, hint: { originalException?: unknown }) {
        // Filtrer les erreurs non critiques
        if (event.exception) {
          const error = hint.originalException;
          
          // Ignorer les erreurs réseau temporaires
          if (error instanceof Error && error.message.includes('NetworkError')) {
            return null;
          }
          
          // Ignorer les erreurs d'annulation AbortController
          if (error instanceof Error && error.name === 'AbortError') {
            return null;
          }
        }
        
        return event;
      },
      
      // Tags par défaut
      initialScope: {
        tags: {
          component: 'ekose-frontend',
          platform: 'web'
        }
      }
    });
  }
};

// Wrapper pour capturer les erreurs manuellement
export const captureError = (error: Error, context?: Record<string, unknown>) => {
  if (import.meta.env.MODE === 'production' && Sentry) {
    Sentry.captureException(error, {
      extra: context
    });
  } else {
    logger.error('🚨 [Development] Error captured:', error, context);
  }
};

// Wrapper pour capturer des messages
export const captureMessage = (message: string, level: 'info' | 'warning' | 'error' = 'info') => {
  if (import.meta.env.MODE === 'production' && Sentry) {
    Sentry.captureMessage(message, level);
  } else {
    logger.debug(`📝 [Development] Message captured [${level}]:`, message);
  }
};
